import React from 'react'
import { useHeloc } from './HelocContext'
import { useTriggerEvents } from '@/hooks/useTriggerEvents'
import {
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  Typography as AscendTypography,
  CircularProgress,
} from '@achieve/ascend'
import { Typography } from 'components/Contentful'
import { US_STATES } from '@/constants'
import { InfoOutlined } from '@mui/icons-material'
import { LinkButton } from 'components/Contentful'
import { Button } from '@achieve/ascend'
import { Refresh } from '@mui/icons-material'
import { emitBannerEvent, BANNER_EVENTS } from 'providers/BannerProvider'

const DEFAULT_MAX_DESIRED_AMOUNT = 700000
const DEFAULT_MIN_DESIRED_AMOUNT = 15000

export const HelocForm = ({ buttonColor, setRecalculate, textContent, config = {}, resultRef }) => {
  const { fireAndForget } = useTriggerEvents()
  const { state, dispatch } = useHeloc()

  const hideBanner = () => emitBannerEvent(BANNER_EVENTS.HIDE)
  const showBanner = () => emitBannerEvent(BANNER_EVENTS.SHOW)

  const defaultValues = {
    minimumHomesCurrentValue: 0,
    maximumHomesCurrentValue: 10000000000,
    minimumMortgageOutstandingBalance: 0,
    maximumMortgageOutstandingBalance: 10000000000,
    minimumDesiredLoanAmount: 0,
    maximumDesiredLoanAmount: 10000000000,
    homesCurrentValueInfo:
      "This is the current value of your home. You can get this from an appraiser, real estate broker, or by searching online for what's my home worth? and providing your address.",
    mortgageOutstandingBalanceInfo:
      'Most lenders require you to have at least 20% equity in your home to qualify for home equity financing.',
    desiredLoanAmountInfo:
      'How much would you like to borrow? Most home equity lenders allow you to borrow up to 80% of your property value including your first mortgage balance. The loan to value requirement can vary with each lender.',
    creditScoreInfo:
      'Lenders generally require a credit score of at least 620, and sometimes higher, to approve home equity financing.',
    selectItemList: [
      { label: 'Excellent (750+)', value: 'excellent' },
      { label: 'Good (680 - 749)', value: 'good' },
      { label: 'Average (620 - 679)', value: 'average' },
      { label: 'Below Average (<620)', value: 'below average' },
    ],
  }

  const calculate = async () => {
    if (resultRef?.current) {
      setTimeout(() => {
        const top = resultRef.current.getBoundingClientRect().top + window.scrollY - 60
        window.scrollTo({ top, behavior: 'smooth' })
      }, 500)
    }
    if (state.isCalculated) {
      dispatch({
        type: 'SET_HELOC_OUTPUT',
        payload: {
          home_value: state.homesCurrentValue,
          balance: state.mortgageOutstandingBalance,
          loan_amount: state.desiredLoanAmount,
          credit_score: state.creditScore,
          monthly_payment: 0,
          apr: 0,
          term: 20,
          state: state.liveState,
        },
      })
      return setRecalculate()
    }
    fireAndForget({ event_type: 'heloc_calc_calculate_button_click' })

    dispatch({ type: 'SET_IS_LOADING', payload: true })

    try {
      dispatch({ type: 'SET_IS_CALCULATED', payload: true })
      dispatch({
        type: 'SET_HELOC_OUTPUT',
        payload: {
          home_value: state.homesCurrentValue,
          balance: state.mortgageOutstandingBalance,
          loan_amount: state.desiredLoanAmount,
          credit_score: state.creditScore,
          monthly_payment: 0,
          apr: 0,
          term: 20,
          state: state.liveState,
        },
      })
    } catch (error) {
      console.error('Error calculating HELOC: ', error)
    } finally {
      dispatch({ type: 'SET_IS_LOADING', payload: false })
    }
  }

  const isFormValid = () => {
    return (
      state.homesCurrentValue > 0 &&
      state.mortgageOutstandingBalance > 0 &&
      state.desiredLoanAmount > 0 &&
      state.creditScore &&
      state.liveState &&
      !state.loanAmountError
    )
  }

  const handleNumericChange = (e, fieldName) => {
    const intValue = e.target.value.replaceAll('$', '').replaceAll(',', '')

    const fieldMap = {
      HomesCurrentValue: 'SET_HOMES_CURRENT_VALUE',
      MortgageOutstandingBalance: 'SET_MORTGAGE_OUTSTANDING_BALANCE',
      DesiredLoanAmount: 'SET_DESIRED_LOAN_AMOUNT',
    }

    const actionType = fieldMap[fieldName]

    if (actionType) {
      dispatch({
        type: actionType,
        payload: validateNumericInput(e.target.value, fieldName),
      })
    } else {
      fireAndForget({
        event_type: 'heloc_calc_field_input',
        field_name: fieldName,
        value: intValue,
        valid: 'false',
      })
    }
  }

  const parseValue = (value) => {
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0
  }

  const validateNumericInput = (value, fieldName) => {
    const numValue = parseValue(value)
    const minValue = defaultValues[`minimum${fieldName}`]
    const maxValue = defaultValues[`maximum${fieldName}`]

    if (numValue < minValue) return minValue
    if (maxValue > 0 && numValue > maxValue) return maxValue

    const intValue = value.replaceAll('$', '').replaceAll(',', '')

    if (fieldName === 'DesiredLoanAmount') {
      if (numValue > (config?.maxDesiredAmount || DEFAULT_MAX_DESIRED_AMOUNT)) {
        fireAndForget({
          event_type: 'heloc_calc_field_input',
          field_name: fieldName,
          value: intValue,
          valid: 'false',
        })
        dispatch({ type: 'SET_LOAN_AMOUNT_ERROR', payload: true })
        dispatch({
          type: 'SET_LOAN_AMOUNT_ERROR_MESSAGE',
          payload: `Your amount exceeds our limit, which is $${(
            config?.maxDesiredAmount || DEFAULT_MAX_DESIRED_AMOUNT
          ).toLocaleString()}`,
        })
      } else if (numValue < (config?.minDesiredAmount || DEFAULT_MIN_DESIRED_AMOUNT)) {
        fireAndForget({
          event_type: 'heloc_calc_field_input',
          field_name: fieldName,
          value: intValue,
          valid: 'false',
        })
        dispatch({ type: 'SET_LOAN_AMOUNT_ERROR', payload: true })
        dispatch({
          type: 'SET_LOAN_AMOUNT_ERROR_MESSAGE',
          payload: `Your amount is below our minimum limit, which is $${(
            config?.minDesiredAmount || DEFAULT_MIN_DESIRED_AMOUNT
          ).toLocaleString()}`,
        })
      } else {
        fireAndForget({
          event_type: 'heloc_calc_field_input',
          field_name: fieldName,
          value: intValue,
          valid: 'true',
        })
        dispatch({ type: 'SET_LOAN_AMOUNT_ERROR', payload: false })
        dispatch({ type: 'SET_LOAN_AMOUNT_ERROR_MESSAGE', payload: '' })
      }
    }

    return numValue
  }

  const handleInfoClick = (fieldName) => {
    dispatch({ type: 'SET_INFO_TEXT', payload: defaultValues[`${fieldName}Info`] })
    dispatch({ type: 'SET_MODAL_INFO_STATUS', payload: true })
  }

  const formatValue = (value) => {
    return `$${Number(value).toLocaleString('en-US')}`
  }

  const handleEstimateClick = () => {
    dispatch({ type: 'SET_MODAL_ESTIMATE', payload: true })
  }

  const selectItems = defaultValues.selectItemList.map(({ label, value }) => (
    <MenuItem key={label} value={value} instanceId={value}>
      <AscendTypography>{label}</AscendTypography>
    </MenuItem>
  ))

  return (
    <>
      <div className="flex flex-col gap-4 w-full" data-testid="heloc-calculator-form-container">
        <div
          className="flex flex-col gap-2"
          data-testid="heloc-calculator-form-home-value-container"
        >
          <div
            className="flex gap-2 items-center"
            data-testid="heloc-calculator-form-home-value-header"
          >
            <Typography
              fontWeight="bold"
              content={textContent?.formHomeValue ?? "What's your home's current value?"}
              className={`${buttonColor ? 'bodyMd font-bold!' : 'bodyMd font-medium'}`}
            />
            <InfoOutlined
              className="text-gray-700 w-4 h-4 cursor-pointer"
              onClick={() => handleInfoClick('homesCurrentValue')}
            />
          </div>
          <TextField
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            data-testid="homesCurrentValue"
            name="homesCurrentValue"
            InputProps={{
              className: 'bg-white',
            }}
            value={state.homesCurrentValue ? formatValue(state.homesCurrentValue) : ''}
            onChange={(e) => handleNumericChange(e, 'HomesCurrentValue')}
            onFocus={() => {
              hideBanner()
            }}
            onBlur={() => {
              showBanner()
            }}
            placeholder="$300,000"
          />
          <AscendTypography className="bodySm text-gray-600" component="span">
            Unsure? Let us{' '}
            <LinkButton
              noLink
              onClick={handleEstimateClick}
              variant="text"
              typographicOptions={{ variant: 'bodySm', fontWeight: 'bold' }}
              className="p-0 min-h-0"
              content={{ linkText: "estimate your home's value" }}
            />
          </AscendTypography>
        </div>
        <div className="flex flex-col gap-2" data-testid="heloc-calculator-form-mortgage-container">
          <div
            className="flex gap-2 items-center"
            data-testid="heloc-calculator-form-mortgage-header"
          >
            <Typography
              content={
                textContent?.formMortgageValue ?? "What's your mortgage's outstanding balance?"
              }
              className={`${buttonColor ? 'bodyMd font-bold!' : 'bodyMd font-medium'}`}
            />
            <InfoOutlined
              className="text-gray-700 w-4 h-4 cursor-pointer"
              onClick={() => handleInfoClick('mortgageOutstandingBalance')}
            />
          </div>
          <TextField
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            data-testid="mortgageOutstandingBalance"
            name="mortgageOutstandingBalance"
            InputProps={{
              className: 'bg-white',
            }}
            value={
              state.mortgageOutstandingBalance ? formatValue(state.mortgageOutstandingBalance) : ''
            }
            onChange={(e) => {
              dispatch({ type: 'SET_EQUITY_ERROR', payload: false })
              handleNumericChange(e, 'MortgageOutstandingBalance')
            }}
            error={state.equityError}
            helperText={
              state.equityError
                ? 'Most lenders require you to have at least 20% equity in your home to qualify for home equity financing.'
                : ''
            }
            onFocus={() => {
              hideBanner()
            }}
            onBlur={() => {
              showBanner()
            }}
            placeholder="Enter amount"
          />
        </div>
        <div
          className="flex flex-col gap-2"
          data-testid="heloc-calculator-form-desired-loan-amount-container"
        >
          <div
            className="flex gap-2 items-center"
            data-testid="heloc-calculator-form-desired-loan-amount-header"
          >
            <Typography
              content={textContent?.formLoanAmount ?? "What's your desired loan amount?"}
              className={`${buttonColor ? 'bodyMd font-bold!' : 'bodyMd font-medium'}`}
            />
            <InfoOutlined
              className="text-gray-700 w-4 h-4 cursor-pointer"
              onClick={() => handleInfoClick('desiredLoanAmount')}
            />
          </div>
          <TextField
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            data-testid="desiredLoanAmount"
            name="desiredLoanAmount"
            InputProps={{
              className: 'bg-white',
            }}
            value={state.desiredLoanAmount ? formatValue(state.desiredLoanAmount) : ''}
            onChange={(e) => handleNumericChange(e, 'DesiredLoanAmount')}
            error={state.loanAmountError}
            helperText={state.loanAmountErrorMessage}
            placeholder="Enter amount"
            onFocus={() => {
              hideBanner()
            }}
            onBlur={() => {
              showBanner()
            }}
          />
        </div>
        <div
          className="flex flex-col gap-2"
          data-testid="heloc-calculator-form-credit-score-container"
        >
          <div
            className="flex gap-2 items-center"
            data-testid="heloc-calculator-form-credit-score-header"
          >
            <Typography
              content={textContent?.formCreditScore ?? "What's your credit score?"}
              className={`${buttonColor ? 'bodyMd font-bold!' : 'bodyMd font-medium'}`}
            />
            <InfoOutlined
              className="text-gray-700 w-4 h-4 cursor-pointer"
              onClick={() => handleInfoClick('creditScore')}
            />
          </div>
          <Select
            className="bg-white rounded-md"
            displayEmpty={true}
            variant="standard"
            value={state.creditScore}
            onChange={(e) => dispatch({ type: 'SET_CREDIT_SCORE', payload: e.target.value })}
            fullWidth={true}
            data-testid="dat-select-field"
          >
            {selectItems}
          </Select>
        </div>
        <div className="flex flex-col gap-2" data-testid="heloc-calculator-form-state-container">
          <div className="flex gap-2 items-center" data-testid="heloc-calculator-form-state-header">
            <Typography
              content={textContent?.formState ?? 'Which state do you live in?'}
              className={`${buttonColor ? 'bodyMd font-bold!' : 'bodyMd font-medium'}`}
            />
          </div>
          <Autocomplete
            className="bg-white rounded-md [&_button]:mt-6"
            options={US_STATES}
            getOptionLabel={(option) => option.label || ''}
            value={US_STATES.find((s) => s.value === state.liveState) || null}
            onChange={(event, newValue) => {
              dispatch({ type: 'SET_LIVE_STATE', payload: newValue?.value || '' })
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select your state"
                variant="standard"
                InputProps={{
                  ...params.InputProps,
                  className: 'bg-white',
                }}
              />
            )}
            fullWidth={true}
            data-testid="dat-select-field"
          />
        </div>
      </div>

      <div
        className="flex flex-col lg:flex-row w-full"
        data-testid="heloc-calculator-form-button-container"
      >
        <Button
          className={
            buttonColor && isFormValid() ? 'mt-4 w-full bg-[#D2E2FE] text-[#154199]' : 'mt-4 w-full'
          }
          type="button"
          color="primary"
          onClick={() => calculate()}
          variant="contained"
          disabled={!isFormValid()}
        >
          {state.isLoading ? 'Calculating...' : state.isCalculated ? 'Re-Calculate' : 'Calculate'}
          {state.isCalculated && !state.isLoading && <Refresh className="w-6 h-6 ms-2" />}
          {state.isLoading && <CircularProgress className="w-6 h-6 ms-2 text-white" size={20} />}
        </Button>
      </div>
    </>
  )
}
